/**
 * Heavily inspired by https://stash.atlassian.com/projects/AID/repos/aid-frontend/browse/src/components/PasswordStrengthMeter.tsx.
 */
import * as React from 'react';
import type { InjectedIntl } from 'react-intl';
import { debounce } from 'lodash';
import makeAsyncScriptLoader from 'react-async-script';
import { MIN_PASSWORD_LENGTH_IN_CHARS } from '@atlassian/help-center-common-util/sign-up-form-validator';
import messages from './messages';
import { Bar, Dividers, Hint } from './styled';
const passwordAnalyzerUrl = 'https://cdnjs.cloudflare.com/ajax/libs/zxcvbn/4.4.2/zxcvbn.js';
const splitter = /\W+/;

const STRENGTH_SCORE_DEBOUNCE_IN_MILLIS = 200;

type PasswordStrengthScore = 0 | 1 | 2 | 3 | 4;

interface PasswordAnalyzerResult {
    score: PasswordStrengthScore;
}

export type PasswordAnalyzer = (password: string, userInputs?: string[]) => PasswordAnalyzerResult;

interface Props {
    intl: InjectedIntl;
    scoreWords: string[];
    password: string;
    zxcvbn?: PasswordAnalyzer;
}

interface State {
    score: PasswordStrengthScore | null;
}

export class PasswordStrengthMeter extends React.Component<Props, State> {
    state = {
        score: null,
    };

    calculateScoreDebounced = debounce(() => {
        const { password, scoreWords, zxcvbn: passwordAnalyzer } = this.props;
        const result = passwordAnalyzer && password ? passwordAnalyzer(password, scoreWords) : null;
        this.setState({
            score: result ? result.score : null,
        });
    }, STRENGTH_SCORE_DEBOUNCE_IN_MILLIS);

    componentDidUpdate = (prevProps: Props) => {
        if (this.props.password === prevProps.password) {
            return;
        }

        if (!this.props.password) {
            this.setState({
                score: null,
            });

            return;
        }

        this.calculateScoreDebounced();
    };

    getStrengthTextForScore = (score: number | null) => {
        const { intl, password, zxcvbn: passwordAnalyzer } = this.props;

        if (!password) {
            return '';
        }

        if (password.length < MIN_PASSWORD_LENGTH_IN_CHARS) {
            return this.props.intl.formatMessage(messages.passwordInvalid);
        }

        if (!passwordAnalyzer) {
            return intl.formatMessage(messages.passwordStrengthUnavailable);
        }

        return score !== null ? this.mapScoreToStrengthText(score) : '';
    };

    mapScoreToStrengthText = (score: number): string => {
        let strengthText;
        switch (score) {
            case 0:
                strengthText = messages.passwordStrengthLevel0;
                break;
            case 1:
                strengthText = messages.passwordStrengthLevel1;
                break;
            case 2:
                strengthText = messages.passwordStrengthLevel2;
                break;
            case 3:
                strengthText = messages.passwordStrengthLevel3;
                break;
            case 4:
                strengthText = messages.passwordStrengthLevel4;
                break;
            default:
                strengthText = messages.passwordStrengthUnavailable;
        }
        return this.props.intl.formatMessage(strengthText);
    };

    render() {
        const { score } = this.state;
        const strengthText = this.getStrengthTextForScore(score);

        return (
            <>
                <Bar score={score}>
                    <Dividers />
                </Bar>
                <Hint>{strengthText}</Hint>
            </>
        );
    }
}

// Suppressing existing violation. Please fix this.
// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment
export const AsyncLoadedPasswordStrengthMeter = makeAsyncScriptLoader(passwordAnalyzerUrl, {
    globalName: 'zxcvbn',
})(PasswordStrengthMeter);

export default AsyncLoadedPasswordStrengthMeter;

const addUnique = (seen: string[], chunk: string) => (seen.includes(chunk) ? seen : [...seen, chunk]);

export function scoreWordSplitter(strings: string[]) {
    const deduped = strings.reduce(addUnique, []);
    return deduped.reduce((arr, str) => str.split(splitter).reduce(addUnique, arr), deduped);
}
